<template>
  <div ref="root">
    <span id="searchLabel" class="sr-only">
      {{ t('search') }}
    </span>
    <auto-complete
      :suggestions="suggestions"
      v-model="fullText"
      input-id="search"
      pt:pcInputText:root:name="fullText"
      pt:pcInputText:root:type="search"
      input-class="search-input w-full min-w-0 flex-auto rounded-none bg-white text-2xl font-normal lg:text-4xl"
      aria-labelledby="searchLabel"
      @keydown="handleKeydown"
      :showEmptyMessage="false"
      @focus="focus = true"
      @blur="focus = false"
      @complete="handleInput"
      @selected="handleSelected"
    >
    </auto-complete>

    <label
      for="search"
      class="whitespace-no-wrap pointer-events-none absolute left-0 top-0 w-full pr-20 text-2xl font-normal lg:text-4xl"
      v-if="!fullText.length"
    >
      {{ t('search') }}
      <span class="hidden opacity-20 lg:inline" v-if="grandTotal">
        {{ t('search_placeholder', { number: grandTotal }) }}
      </span>
    </label>
    <button @click="reset" type="reset" class="px-6" v-if="fullText">
      <span class="sr-only">{{ t('reset') }} </span>
      <svg
        class="search-close-icon"
        focusable="false"
        aria-hidden="true"
        width="20"
        height="20"
      >
        <use xlink:href="#close" />
      </svg>
    </button>
    <button type="submit" class="-mr-3 px-3" @click="triggerSearch">
      <span class="sr-only">{{ t('search') }} </span>
      <svg
        class="search-icon"
        focusable="false"
        aria-hidden="true"
        width="20"
        height="20"
      >
        <use xlink:href="#magnifier" />
      </svg>
    </button>
  </div>
</template>

<i18n>
{
  "en": {
    "search": "Search",
    "reset": "Reset",
    "search_placeholder": "in {number} objects"
  },
  "de": {
    "search": "Suche",
    "reset": "Zurücksetzen",
    "search_placeholder": "in {number} Objekten"
  }
}
</i18n>
<script lang="ts">
import { AxiosInstance } from 'axios';
import AutoComplete from 'primevue/autocomplete';
import {
  Ref,
  computed,
  defineComponent,
  getCurrentInstance,
  onMounted,
  ref,
} from 'vue';
import autocompletionsQuery from '../gql/autocompletionsQuery.gql';
import { basicFilter } from '../utils/filter';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  components: {
    AutoComplete,
  },
  props: {
    grandTotal: {
      type: Number,
      default: 0,
    },
  },

  setup(props, context) {
    const instance = getCurrentInstance();
    const api: Ref<AxiosInstance | null> = ref(null);
    const fullText = ref(basicFilter.fullText || '');
    const autocompletions: Ref<string[]> = ref([]);
    const root: Ref<HTMLElement | null> = ref(null);
    let timeout: any;

    onMounted(() => {
      context.emit('mounted');

      instance?.proxy.$nextTick(() => {
        if (!fullText.value) {
          const input = document.getElementById('search');

          if (input) {
            input.focus();
          }
        }
      });
    });

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const form = e.target.closest('form');
        if (form.hasAttribute('action')) {
          form.submit();
        } else {
          triggerSearch();
        }
      }
    };

    const fetchAutocompletions = async () => {
      const { executeGqlQuery, createApi } = await import(
        /* webpackChunkName: "gql" */ '../utils/gql'
      );
      if (!api.value) {
        api.value = createApi();
      }

      if (api.value) {
        executeGqlQuery(
          api.value,
          autocompletionsQuery,
          {
            word: fullText.value,
          },
          (data: any) => {
            autocompletions.value = data.autocompletions;
          }
        );
      }
    };

    const suggestions = computed(() => autocompletions.value);

    const handleInput = (): void => {
      if (!fullText.value) {
        autocompletions.value = [];
      } else {
        fetchAutocompletions();
      }
    };

    const focus = ref(false);

    const showLabel = computed(() => {
      return !focus.value && !fullText.value;
    });

    const reset = () => {
      basicFilter.fullText = '';
      fullText.value = '';
      autocompletions.value = [];
    };

    const triggerSearch = () => {
      if (root.value) {
        const formElement = root.value.closest('form');

        if (formElement && formElement.getAttribute('action')) {
          instance?.proxy.$nextTick(() => {
            formElement.submit();
          });
        } else {
          basicFilter.fullText = fullText.value;
        }
      }
    };

    const handleSelected = (value: any) => {
      if (value) {
        fullText.value = value.item || '';
      }

      triggerSearch();
    };

    return {
      focus,
      showLabel,
      fullText,
      reset,
      handleInput,
      suggestions,
      handleSelected,
      handleKeydown,
      triggerSearch,
      root,
      ...useI18n(),
    };
  },
});
</script>
